import { Box } from '../primitives/Box.js';
import { Pressable } from '../primitives/Pressable.js';
import { Text } from '../primitives/Text.js';
import { cx } from '../primitives/cx.js';
import { DAY_TRACK, TODAY_TRACK } from './rowMetrics.js';

export type DayCellState = 'done' | 'floor' | 'missed' | 'off' | 'future';

export type DayColumn = {
  key: string;
  /** Already formatted by the caller — "M", "T"… in the reader's locale. */
  weekday: string;
  day: string;
  isToday: boolean;
  /** Inside the backfill window, so this day can still be ticked. */
  editable: boolean;
};

export type DayCell = { state: DayCellState; label: string };

/**
 * One habit's past, as a strip of days beside its name.
 *
 * This is the wall chart students already screenshot: the row is still the
 * habit row, it just carries its own history to the right of the name. TODAY
 * IS NOT IN HERE — dates run oldest → today, and the caller puts the ordinary
 * completion circle in the last track, so the primary daily action is the same
 * control on a phone and on a laptop.
 *
 * Track widths come from `rowMetrics` — the one place the strip and the date
 * header above it agree on geometry.
 */

const fillClass: Record<DayCellState, string> = {
  done: 'bg-accent',
  // Kept at the floor: the same accent, drawn as a ring rather than a solid
  // square. Distinguished by SHAPE first — at 16px a colourblind reader and a
  // sighted one both see an outline against a fill.
  floor: 'bg-transparent ring-2 ring-inset ring-accent',
  // Scheduled and skipped. Neutral and filled, never red: the strip is a
  // record, not a scolding. Filled rather than outlined because a 1px outline
  // at this size disappears into the card.
  missed: 'bg-line',
  // Nothing was due. Recessed, so the eye skips it.
  off: 'bg-sunken',
  future: 'bg-sunken opacity-40',
};

export type DayStripProps = {
  columns: readonly DayColumn[];
  cells: readonly DayCell[];
  /** Omit to render read-only. Only `editable` columns ever call it. */
  onToggle?: (columnIndex: number) => void;
  className?: string;
};

export function DayStrip({ columns, cells, onToggle, className }: DayStripProps) {
  return (
    <Box className={cx('flex shrink-0 items-stretch', className)}>
      {columns.map((column, index) => {
        const cell = cells[index];
        if (!cell) return null;

        const track = cx(DAY_TRACK, 'grid shrink-0 place-items-center self-stretch');
        const dot = cx('size-4 rounded-full', fillClass[cell.state]);

        return column.editable && onToggle ? (
          <Pressable
            key={column.key}
            compact
            role="gridcell"
            aria-label={cell.label}
            aria-pressed={cell.state === 'done' || cell.state === 'floor'}
            onClick={() => onToggle(index)}
            className={track}
          >
            <span className={dot} />
          </Pressable>
        ) : (
          <Box key={column.key} role="gridcell" aria-label={cell.label} className={track}>
            <span className={dot} />
          </Box>
        );
      })}
    </Box>
  );
}

export type DayStripHeaderProps = {
  columns: readonly DayColumn[];
  className?: string;
};

/** The dates above the strips. Same tracks, so the columns line up. */
export function DayStripHeader({ columns, className }: DayStripHeaderProps) {
  return (
    <Box className={cx('flex shrink-0 items-end', className)}>
      {columns.map((column) => (
        <Box
          key={column.key}
          className={cx(
            column.isToday ? TODAY_TRACK : DAY_TRACK,
            'flex shrink-0 flex-col items-center gap-0.5 pb-1',
          )}
        >
          <Text variant="caption" tone={column.isToday ? 'accent' : 'subtle'}>
            {column.weekday}
          </Text>
          <Text variant="caption" tone={column.isToday ? 'accent' : 'muted'} numeric>
            {column.day}
          </Text>
        </Box>
      ))}
    </Box>
  );
}
